import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  User,
  Smartphone,
  Mail,
  Lock,
  ShieldCheck,
  Calendar,
  Venus,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle,
  Gift,
} from "lucide-react";
import AuthLayout from "../helpers/AuthLayout";
import SummaryApi from "../common/index";
import toast, { Toaster } from "react-hot-toast";
import { MdOutlineErrorOutline } from "react-icons/md";

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState(() => {
    const savedData = sessionStorage.getItem("signupFormData");
    return savedData
      ? JSON.parse(savedData)
      : {
          fullName: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: "",
          gender: "",
          dob: "",
          organization_id: "",
          referral_code: "",
           entry_coupon_code: "",
          agreeTerms: false,
        };
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    sessionStorage.setItem("signupFormData", JSON.stringify(formData));
  }, [formData]);
  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref") || params.get("referral") || params.get("coupon");
  if (ref) {
    setFormData(prev => ({ 
      ...prev, 
      referral_code: ref,
      entry_coupon_code: ref 
    }));
  }
}, []);
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "fullName":
        if (!value.trim()) error = "Full name is required";
        else if (value.trim().length < 2) error = "Name is too short";
        break;
      case "email":
        if (!value) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = "Invalid email";
        break;
      case "phone":
        if (!value) error = "Mobile number is required";
        else if (!/^\d{10}$/.test(value)) error = "Enter valid 10-digit number";
        break;
      case "password":
        if (!value) {
          error = "Password is required";
        } else {
          // Mixed case, numbers, special chars, min 8
          const strongPasswordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
          if (!strongPasswordRegex.test(value)) {
            error = "8+ chars, mixed case, number & special char";
          }
        }
        break;
      case "confirmPassword":
        if (!value) error = "Confirm password";
        else if (value !== formData.password) error = "Passwords do not match";
        break;
      case "dob":
        if (value) {
          if (!/^\d{2}-\d{2}-\d{4}$/.test(value)) {
            error = "Format must be DD-MM-YYYY";
          } else {
            const [day, month, year] = value.split("-").map(Number);
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();

            // Basic date validity check
            const isValidDate =
              birthDate.getFullYear() === year &&
              birthDate.getMonth() === month - 1 &&
              birthDate.getDate() === day &&
              year >= 1900;

            if (!isValidDate) {
              error = "Invalid date";
            } else {
              // Age calculation
              let age = today.getFullYear() - birthDate.getFullYear();
              const m = today.getMonth() - birthDate.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }

              if (age < 18) {
                error = "You must be at least 18 years old";
              }
            }
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // List only strictly mandatory fields here
    const mandatory = [
      "fullName",
      "email",
      "phone",
      "password",
      "confirmPassword",
    ];

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      } else if (mandatory.includes(key) && !formData[key]) {
        newErrors[key] = "Required field";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;

    // New logic for checkbox
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

      if (name === "referral_code") {
    setFormData(prev => ({ 
      ...prev, 
      referral_code: value, 
      entry_coupon_code: value 
    }));
    return; // exit early
  }
    if (name === "dob") {
      value = value.replace(/\D/g, "");
      if (value.length > 2 && value.length <= 4) {
        value = `${value.slice(0, 2)}-${value.slice(2)}`;
      } else if (value.length > 4) {
        value = `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4, 8)}`;
      }
    }

    if (name === "phone" && !/^\d*$/.test(value)) return;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "phone" ? value.slice(0, 10) : value,
    }));

    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(
      Object.keys(formData).reduce((a, c) => ({ ...a, [c]: true }), {}),
    );

    if (validateForm()) {
      setLoading(true);

      // Convert DD-MM-YYYY to YYYY-MM-DD
      let formattedDob = "";
      if (formData.dob) {
        const [day, month, year] = formData.dob.split("-");
        formattedDob = `${year}-${month}-${day}`;
      }

      const payload = {
        user_name: formData.fullName,
        mobile_number: formData.phone,
        alternate_mobile_number: "",
        email: formData.email,
        gender: formData.gender,
        date_of_birth: formattedDob,
        password: formData.password,
        organization_id: formData.organization_id || "0",
        referral_code: formData.referral_code,
        entry_coupon_code: formData.entry_coupon_code,
      };

      try {
        const response = await fetch(SummaryApi.userRegistration.url, {
          method: SummaryApi.userRegistration.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const result = await response.json();

        if (result.status) {
          // --- NEW LOGIC START: Handle Auto-Subscription ---
          const { user_id, organization_id } = result;

          if (organization_id && organization_id !== "0") {
            try {
              // 1. Fetch the organization's active subscription details
              const subRes = await fetch(SummaryApi.getActiveOrganizationSubscription.url, {
                method: SummaryApi.getActiveOrganizationSubscription.method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ organization_id: organization_id }),
              });
              const subData = await subRes.json();

              if (subData.status && subData.data.length > 0) {
                // Find the first active plan
                const activePlan = subData.data.find(s => s.status === "Active") || subData.data[0];
                 const startDate = new Date();
      const todayFormatted = startDate.toISOString().split("T")[0];

                const durationStr = activePlan.duration || "1 month"; // Fallback
      const durationValue = parseInt(durationStr.match(/\d+/)); // Extracts the number (6)
      const isYear = durationStr.toLowerCase().includes("year");
      const isMonth = durationStr.toLowerCase().includes("month");

       const endDate = new Date(startDate);
      if (isYear) {
        endDate.setFullYear(startDate.getFullYear() + durationValue);
      } else if (isMonth) {
        endDate.setMonth(startDate.getMonth() + durationValue);
      } else {
        // Default to days if no month/year found
        endDate.setDate(startDate.getDate() + durationValue);
      }
      const endDateFormatted = endDate.toISOString().split("T")[0];
                // 2. Create the subscription for this specific user
                const subPayload = {
                  user_id: user_id,
                  plan_id: activePlan.plan_id,
                  package_id: activePlan.package_id,
                  package_start_date: todayFormatted,
        package_end_date: endDateFormatted,
                  payment_mode: "Online",
                  is_payment_success: 1,
                  organization_id: organization_id,
                };

                await fetch(SummaryApi.organizationUserSubscriptionCreate.url, {
                  method: SummaryApi.organizationUserSubscriptionCreate.method,
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(subPayload),
                });
              }
            } catch (subErr) {
              console.error("Auto-subscription failed:", subErr);
              // We don't block the user if subscription fails, 
              // as the account is already created.
            }
          }
          // --- NEW LOGIC END ---

          sessionStorage.removeItem("signupFormData");
          toast.custom(
            (t) => (
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                className={`${
                  t.visible ? "animate-enter" : "animate-leave"
                } max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] pointer-events-auto flex ring-1 ring-black ring-opacity-5 overflow-hidden border-b-4 border-rose-500 z-[100]`}
              >
                <div className="flex-1 w-0 p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                      <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
                        <CheckCircle size={28} />
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-bold text-rose-950">
                        Welcome to the family!
                      </p>
                      <p className="mt-1 text-xs text-rose-900/50 font-medium">
                        Account created {organization_id && organization_id !== "0" ? "and plan activated." : "successfully."}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex border-l border-rose-50">
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-bold text-rose-600 hover:text-rose-500 focus:outline-none"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            ),
            { duration: 2000 },
          );

          setTimeout(() => {
            navigate("/login", { state: { fromSignup: true } });
          }, 2000);
        } else {
          // Handle failure toast... (rest of existing code)
        }
      } catch (error) {
        toast.error("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <AuthLayout>
      {/* <Toaster position="top-center" reverseOrder={false} /> */}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl bg-white/20 backdrop-blur-[25px] border border-white/40 rounded-[2rem] p-10 shadow-2xl"
      >
        <h2 className="text-3xl font-bold text-rose-900 text-center mb-8 tracking-widest uppercase">
          Register
        </h2>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <Field
            label="Full Name"
            name="fullName"
            icon={<User size={18} />}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />
          <Field
            label="Email Address"
            name="email"
            type="email"
            icon={<Mail size={18} />}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />
          <Field
            label="Mobile Number"
            name="phone"
            icon={<Smartphone size={18} />}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />

          <PasswordField
            label="Password"
            name="password"
            isVisible={showPassword}
            setIsVisible={setShowPassword}
            icon={<Lock size={18} />}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />

          <PasswordField
            label="Confirm Password"
            name="confirmPassword"
            isVisible={showConfirmPassword}
            setIsVisible={setShowConfirmPassword}
            icon={<ShieldCheck size={18} />}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />

          <SelectField
            label="Gender (Optional)"
            name="gender"
            icon={<Venus size={18} />}
            required={false}
            {...{ formData, handleChange }}
          />

          {/* UPDATED: Date Field with clickable calendar icon */}
          <DateField
            label="Date of Birth (Optional)"
            name="dob"
            icon={<Calendar size={18} />}
            required={false}
            {...{ formData, handleChange, errors, touched }}
          />

          <Field
           label="Referral Code (Optional)"
            name="referral_code"
            icon={<Gift size={18} />}
            required={false}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />
          <Field
           label="Coupon Code (Optional)"
            name="entry_coupon_code"
            icon={<Gift size={18} />}
            required={false}
            {...{ formData, errors, touched, handleChange, handleBlur }}
          />
          {/* --- Terms and Conditions Checkbox --- */}
          <div className="md:col-span-2 flex items-center gap-3 py-2">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                id="agreeTerms"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                className="w-5 h-5 rounded border-2 border-rose-300 text-rose-600 focus:ring-rose-500 accent-rose-500 cursor-pointer"
              />
            </div>
            <label
              htmlFor="agreeTerms"
              className="text-sm font-semibold text-rose-900 cursor-pointer select-none"
            >
              I agree to the
              <Link
                to="/terms-conditions"
                className="ml-1 text-rose-600 hover:text-rose-800 underline"
              >
                Terms & Conditions
              </Link>
            </label>
          </div>

          {/* --- Submit Button --- */}
          <motion.button
            type="submit"
            // ENABLED ONLY IF NOT LOADING AND TERMS ARE CHECKED
            disabled={loading || !formData.agreeTerms}
            className={`md:col-span-2 w-full flex justify-center items-center gap-2 bg-gradient-to-r from-rose-400 to-rose-600
  text-white font-bold py-4 rounded-xl shadow-lg transition-all 
  ${loading || !formData.agreeTerms ? "opacity-50 cursor-not-allowed grayscale" : "hover:shadow-rose-200 hover:scale-[1.01] active:scale-95"}`}
          >
            {loading && <Loader2 className="animate-spin" size={20} />}
            {loading ? "Processing..." : "Create Account"}
          </motion.button>
        </form>

        <div className="mt-8 text-center text-sm font-semibold text-rose-950">
          Already a Member?
          <Link
            to="/login"
            state={{ fromSignup: true }} // Pass state here
            className="ml-1 border-b-2 border-rose-900 font-bold hover:text-rose-700 transition-colors"
          >
            Login
          </Link>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

/* ---------- Reusable Components ---------- */

const Field = ({
  label,
  name,
  type = "text",
  icon,
  formData,
  errors,
  touched,
  handleChange,
  handleBlur,
  required = true,
}) => (
  <div>
    <label className="text-sm font-medium text-rose-900">
      {label} {required && <span className="text-rose-600">*</span>}
    </label>
    <div className="relative border-b-2 border-rose-900/20 py-2">
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        className="w-full bg-transparent outline-none text-rose-950 placeholder-rose-300 pr-8"
        autoComplete="off"
      />
      <div className="absolute right-0 top-2 text-rose-900/60">{icon}</div>
    </div>
    <div className="h-4">
      {touched[name] && errors[name] && (
        <p className="text-red-600 text-xs mt-1 font-medium">{errors[name]}</p>
      )}
    </div>
  </div>
);

const PasswordField = ({
  label,
  name,
  isVisible,
  setIsVisible,
  icon,
  formData,
  errors,
  touched,
  handleChange,
  handleBlur,
}) => (
  <div>
    <label className="text-sm font-medium text-rose-900">
      {label} <span className="text-rose-600">*</span>
    </label>
    <div className="relative border-b-2 border-rose-900/20 py-2">
      <input
        type={isVisible ? "text" : "password"}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        className="w-full bg-transparent outline-none text-rose-950 placeholder-rose-300 pr-14"
      />
      <div className="absolute right-0 top-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsVisible(!isVisible)}
          className="text-rose-900/40 hover:text-rose-900/80 focus:outline-none"
        >
          {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
        <div className="text-rose-900/60">{icon}</div>
      </div>
    </div>
    <div className="h-4">
      {touched[name] && errors[name] && (
        <p className="text-red-600 text-xs mt-1 font-medium">{errors[name]}</p>
      )}
    </div>
  </div>
);

const SelectField = ({
  label,
  name,
  icon,
  formData,
  handleChange,
  required = true,
}) => (
  <div>
    <label className="text-sm font-medium text-rose-900">
      {label} {required && <span className="text-rose-600">*</span>}
    </label>
    <div className="relative border-b-2 border-rose-900/20 py-2">
      <select
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className="w-full bg-transparent outline-none appearance-none text-rose-950"
      >
        <option value="">Select Gender</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="Other">Other</option>
      </select>
      <div className="absolute right-0 top-2 text-rose-900/60 pointer-events-none">
        {icon}
      </div>
    </div>
    <div className="h-4" />
  </div>
);

const DateField = ({
  label,
  name,
  icon,
  formData,
  handleChange,
  errors,
  touched,
  required = true,
}) => {
  const dateInputRef = useRef(null);

  // Calculate the date 18 years ago for the native picker's max attribute
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  )
    .toISOString()
    .split("T")[0];

  const handleIconClick = () => {
    if (dateInputRef.current) {
      if (typeof dateInputRef.current.showPicker === "function") {
        dateInputRef.current.showPicker();
      } else {
        dateInputRef.current.focus();
      }
    }
  };

  const handlePickerChange = (e) => {
    const pickerVal = e.target.value; // Format: YYYY-MM-DD
    if (!pickerVal) return;
    const [year, month, day] = pickerVal.split("-");

    // Simulate the text input format to trigger existing dash logic
    const formattedValue = `${day}-${month}-${year}`;

    // Call handleChange with the formatted string
    handleChange({ target: { name, value: formattedValue } });
  };

  return (
    <div>
      <label className="text-sm font-medium text-rose-900">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      <div className="relative border-b-2 border-rose-900/20 py-2">
        <input
          type="text"
          placeholder="DD-MM-YYYY"
          maxLength={10}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          onBlur={(e) => handleChange(e)} // Optional: trigger validation on blur
          className="w-full bg-transparent outline-none text-rose-950 pr-8"
        />

        <input
          type="date"
          ref={dateInputRef}
          max={maxDate} // Prevents selecting users < 18 in the calendar
          onChange={handlePickerChange}
          className="absolute opacity-0 pointer-events-none w-0 h-0"
        />

        <button
          type="button"
          onClick={handleIconClick}
          className="absolute right-0 top-2 text-rose-900/60 hover:text-rose-900 transition-colors"
        >
          {icon}
        </button>
      </div>
      <div className="h-4">
        {touched[name] && errors[name] && (
          <p className="text-red-600 text-xs mt-1 font-medium">
            {errors[name]}
          </p>
        )}
      </div>
    </div>
  );
};

export default Signup;
